import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/lib/motion";
import { FileText, Download, X } from "lucide-react";
import { useSiteContent } from "@/lib/useSiteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MarketInsights() {
  const c = useSiteContent().insights;
  const resources = c.resources || [];
  const [activeIdx, setActiveIdx] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openForm = (i) => {
    setActiveIdx(i);
    setForm({ name: "", email: "", phone: "" });
  };
  const closeForm = () => setActiveIdx(null);

  const submit = async (e) => {
    e.preventDefault();
    if (activeIdx === null) return;
    setLoading(true);
    try {
      await axios.post(`${API}/insights/request`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        resource: resources[activeIdx].title,
      });
      toast.success("Thank you. Our team will send the document shortly.");
      closeForm();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="insights" data-testid="insights-section" className="section-pad bg-[#FAFAF8]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="md:max-w-3xl">
          <Reveal><p className="eyebrow">{c.eyebrow}</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif-luxe text-[#1D1D1F] text-4xl md:text-5xl leading-[1.08] mt-5">
              {c.heading}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#616161] mt-6 max-w-2xl leading-relaxed">{c.intro}</p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {resources.map((r, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <div data-testid={`insight-card-${i}`} className="card-luxe flex flex-col justify-between h-full">
                <div>
                  <FileText size={24} strokeWidth={1.1} className="text-[#B68D40] mb-6" />
                  <div className="text-[0.7rem] tracking-[0.16em] uppercase text-[#616161]">{r.tag}</div>
                  <h3 className="font-serif-luxe text-2xl text-[#1D1D1F] mt-3 leading-tight">{r.title}</h3>
                </div>

                <button
                  onClick={() => openForm(i)}
                  data-testid={`insight-request-${i}`}
                  className="mt-8 inline-flex items-center gap-2 text-[#1D1D1F] link-gold text-[0.78rem] tracking-[0.16em] uppercase self-start"
                >
                  Request Access <Download size={14} strokeWidth={1.4} />
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {activeIdx !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={closeForm}
          data-testid="insight-modal"
        >
          <div
            className="bg-white w-full max-w-lg relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-6 md:p-8 border-b border-black/10">
              <div>
                <p className="eyebrow">Request Access</p>
                <h3 className="font-serif-luxe text-2xl md:text-3xl text-[#1D1D1F] mt-2 leading-tight">
                  {resources[activeIdx]?.title}
                </h3>
                <p className="text-xs text-[#616161] mt-2">
                  Share your details and we&rsquo;ll email you the document within 24 hours.
                </p>
              </div>
              <button
                onClick={closeForm}
                className="text-[#616161] hover:text-[#1D1D1F] shrink-0 ml-4"
                data-testid="insight-modal-close"
                aria-label="Close"
              >
                <X size={20} strokeWidth={1.4} />
              </button>
            </div>

            <form onSubmit={submit} className="p-6 md:p-8 space-y-6" data-testid="insight-form">
              <div>
                <label className="text-[0.7rem] tracking-[0.16em] uppercase text-[#616161]">
                  Full Name
                </label>
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="input-luxe"
                  data-testid="insight-name"
                />
              </div>
              <div>
                <label className="text-[0.7rem] tracking-[0.16em] uppercase text-[#616161]">
                  Email
                </label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="input-luxe"
                  data-testid="insight-email"
                />
              </div>
              <div>
                <label className="text-[0.7rem] tracking-[0.16em] uppercase text-[#616161]">
                  Phone Number
                </label>
                <input
                  required
                  value={form.phone}
                  onChange={update("phone")}
                  className="input-luxe"
                  data-testid="insight-phone"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={closeForm} className="btn-outline-dark" data-testid="insight-cancel">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="btn-luxe" data-testid="insight-submit">
                  {loading ? "Sending…" : "Send it to me"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
